/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useMemo } from 'react';

import Highcharts, { Options } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { Nullable } from '../../../utils/types';
import { FREE_MONEY_COLOR, OTHER_POSITIONS_COLOR, getPortfolioColor } from '../colors';

export const FREE_MONEY = 'Свободные средства';
export const OTHER_POSITIONS = 'Остальные';

export type PiePosition = {
  symbol: string | null;
  name: Nullable<string>;
  number?: number;
  currency?: string;
  weight: number;
  avgPrice?: number;
  lastPrice?: number;
};

type Props = {
  data: PiePosition[];
  activeIndex?: number | null;
  onSliceHover?: (index: number | null) => void;
};

export const PieChart: React.FC<Props> = ({ data, activeIndex = null, onSliceHover }) => {
  const dataSeries = useMemo(
    () =>
      data.map((item, key) => {
        const baseColor =
          item.name === FREE_MONEY
            ? FREE_MONEY_COLOR
            : item.name === OTHER_POSITIONS
            ? OTHER_POSITIONS_COLOR
            : getPortfolioColor(key);
        const isActive = activeIndex === null || activeIndex === key;

        return {
          color: isActive ? baseColor : Highcharts.color(baseColor).setOpacity(0.45).get(),
          borderColor: isActive ? '#ffffff' : 'transparent',
          borderWidth: activeIndex === key ? 3 : 0,
          name: item.name as string,
          symbol: item.symbol,
          weight: parseFloat(item.weight.toFixed(2)),
          y: parseFloat(Math.abs(item.weight).toFixed(2)),
          sliced: activeIndex === key,
        };
      }),
    [activeIndex, data],
  );

  const options: Options = {
    chart: {
      type: 'pie',
      spacing: [8, 0, 8, 0],
      height: '100%',
      backgroundColor: 'transparent',
    },
    plotOptions: {
      pie: {
        dataLabels: {
          enabled: false,
        },
        size: '84%',
        innerSize: '62%',
        borderRadius: 2,
        slicedOffset: 6,
        states: {
          inactive: {
            opacity: 1,
          },
          hover: {
            halo: {
              size: 0,
              opacity: 0,
            },
            enabled: true,
            brightness: 0,
          },
          select: {
            enabled: false,
          },
        },
        point: {
          events: {
            mouseOver() {
              onSliceHover?.(this.index);
            },
            mouseOut() {
              onSliceHover?.(null);
            },
          },
        },
        tooltip: {
          headerFormat: '',
          // @ts-ignore Highcharts pie tooltip supports HTML content here.
          useHTML: true,
          pointFormatter: function () {
            const point = this as Highcharts.Point & {
              symbol?: string | null;
              weight?: number;
            };
            const title = point.symbol || point.name;
            const subtitle =
              point.name === OTHER_POSITIONS
                ? 'Суммарная доля оставшихся позиций'
                : point.symbol && point.name !== point.symbol
                ? point.name
                : '';

            return `
              <div class="pie-chart-tooltip">
                <div class="pie-chart-tooltip__title">${title}</div>
                ${subtitle ? `<div class="pie-chart-tooltip__subtitle">${subtitle}</div>` : ''}
                <div class="pie-chart-tooltip__value">${Highcharts.numberFormat(point.weight ?? point.y ?? 0, 2)}%</div>
              </div>
            `;
          },
          padding: 0,
          outside: false,
          borderWidth: 0,
          shadow: false,
          backgroundColor: 'transparent',
        },
      },
    },
    credits: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    title: {
      text: '',
    },
    // @ts-ignore
    series: [
      {
        type: 'pie',
        data: dataSeries,
      },
    ],
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};
